import { useMutation, useQuery } from '@tanstack/react-query';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';

export type H1ReportSource = 'hacker-api' | 'org-api';

export type H1SeverityRating = 'none' | 'low' | 'medium' | 'high' | 'critical';

export interface H1Activity {
  id: string;
  type: string;
  attributes: {
    message: string;
    created_at: string;
    updated_at?: string;
    internal?: boolean;
  };
  relationships?: {
    actor?: {
      data?: {
        id: string;
        type: string;
      };
    };
  };
}

/**
 * Update the severity rating of a HackerOne report via the Org API.
 *
 * Calls POST /hackerone/severity with { report_id, rating, source: 'org-api' }.
 * Only supported when source === 'org-api'; the backend returns 400 otherwise.
 *
 * On success, invalidates ['hackerone', 'activities', reportId, 'org-api'] so
 * the severity-change activity appears in the timeline.
 */
export function useUpdateHackerOneSeverity(reportId: string) {
  const axios = useAxios();

  return useMutation<void, Error, { rating: H1SeverityRating }>({
    mutationFn: async ({ rating }) => {
      await axios.post('/hackerone/severity', {
        report_id: reportId,
        rating,
        source: 'org-api',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['hackerone', 'activities', reportId, 'org-api'],
      });
    },
  });
}

/**
 * Fetch live HackerOne activities for a given report.
 *
 * Calls GET /hackerone/activities?reportId={reportId}&source={source}.
 * The backend returns the array directly with no envelope wrapper.
 *
 * Query key: ['hackerone', 'activities', reportId, source]
 */
export function useHackerOneActivities(
  reportId: string | undefined,
  source: H1ReportSource,
  enabled: boolean
) {
  const axios = useAxios();

  return useQuery<H1Activity[]>({
    queryKey: ['hackerone', 'activities', reportId, source],
    queryFn: async () => {
      const { data } = await axios.get<H1Activity[]>('/hackerone/activities', {
        params: { reportId, source },
      });
      return data;
    },
    enabled,
  });
}
